import {
	useOverlayMetadata,
	type MetadataSet,
	type OverlayMetadata,
	type ProductInfo,
} from '@globus/image-overlay';

export type LeafletPageWithMetadata = {
	metadata: MetadataSet;
};

export type LeafletProductNavigationOptions = {
	onPageChange?: (pageNumber: number) => void;
};

export const leafletProductNavigationKey: InjectionKey<ReturnType<typeof useLeafletProductNavigation>> = Symbol('leafletProductNavigation');

type PagePosition = {
	pageIndex: number;
	productIndex: number;
};

function extractPageProducts(metadata: OverlayMetadata | null): ProductInfo[] {
	return (metadata?.items ?? [])
		.filter((item) => item.productInfo)
		.map((item) => item.productInfo!);
}

function findProductIndex(products: ProductInfo[], product: ProductInfo): number {
	const byEan = products.findIndex((entry) => entry.EAN === product.EAN);

	if (byEan >= 0) {
		return byEan;
	}

	return products.findIndex((entry) => entry.Name === product.Name);
}

export function useLeafletProductNavigation(
	pages: Ref<LeafletPageWithMetadata[]>,
	options: LeafletProductNavigationOptions = {},
) {
	const isOpen = ref(false);
	const selectedProductInfo = ref<ProductInfo | null>(null);
	const currentPageIndex = ref(0);
	const currentProductIndex = ref(0);
	const canGoNext = ref(false);
	const canGoPrev = ref(false);

	const metadataCache = new Map<number, OverlayMetadata>();
	const {fetch: fetchMetadata, metadata: loadedMetadata} = useOverlayMetadata();

	const totalPages = computed(() => pages.value.length);

	async function ensurePageMetadata(pageIndex: number): Promise<OverlayMetadata | null> {
		if (metadataCache.has(pageIndex)) {
			return metadataCache.get(pageIndex) ?? null;
		}

		const metadataSet = pages.value[pageIndex]?.metadata;

		if (!metadataSet?.original) {
			return null;
		}

		await fetchMetadata(metadataSet);

		const metadata = loadedMetadata.value;

		if (metadata) {
			metadataCache.set(pageIndex, metadata);
		}

		return metadata;
	}

	async function getPageProducts(pageIndex: number): Promise<ProductInfo[]> {
		return extractPageProducts(await ensurePageMetadata(pageIndex));
	}

	function scrollToPage(pageIndex: number) {
		options.onPageChange?.(pageIndex + 1);
	}

	async function resolveStep(delta: 1 | -1): Promise<PagePosition | null> {
		const page = currentPageIndex.value;
		const index = currentProductIndex.value;
		const products = await getPageProducts(page);
		const nextIndex = index + delta;

		if (nextIndex >= 0 && nextIndex < products.length) {
			return {pageIndex: page, productIndex: nextIndex};
		}

		if (delta > 0) {
			for (let nextPage = page + 1; nextPage < totalPages.value; nextPage++) {
				const nextProducts = await getPageProducts(nextPage);

				if (nextProducts.length > 0) {
					return {pageIndex: nextPage, productIndex: 0};
				}
			}

			return null;
		}

		for (let previousPage = page - 1; previousPage >= 0; previousPage--) {
			const previousProducts = await getPageProducts(previousPage);

			if (previousProducts.length > 0) {
				return {pageIndex: previousPage, productIndex: previousProducts.length - 1};
			}
		}

		return null;
	}

	async function updateNavigationAvailability() {
		if (!isOpen.value) {
			canGoNext.value = false;
			canGoPrev.value = false;

			return;
		}

		const [next, prev] = await Promise.all([resolveStep(1), resolveStep(-1)]);
		canGoNext.value = next !== null;
		canGoPrev.value = prev !== null;
	}

	async function selectAt(pageIndex: number, productIndex: number) {
		const products = await getPageProducts(pageIndex);

		if (!products[productIndex]) {
			return;
		}

		currentPageIndex.value = pageIndex;
		currentProductIndex.value = productIndex;
		selectedProductInfo.value = products[productIndex];
		await updateNavigationAvailability();
	}

	async function open(pageIndex: number, productInfo: ProductInfo) {
		const products = await getPageProducts(pageIndex);
		const productIndex = findProductIndex(products, productInfo);

		if (productIndex < 0) {
			return;
		}

		currentPageIndex.value = pageIndex;
		currentProductIndex.value = productIndex;
		selectedProductInfo.value = products[productIndex];
		isOpen.value = true;
		await updateNavigationAvailability();
	}

	function close() {
		isOpen.value = false;
		selectedProductInfo.value = null;
		canGoNext.value = false;
		canGoPrev.value = false;
	}

	async function navigateByStep(delta: 1 | -1) {
		const position = await resolveStep(delta);

		if (!position) {
			return;
		}

		const pageChanged = position.pageIndex !== currentPageIndex.value;

		await selectAt(position.pageIndex, position.productIndex);

		if (pageChanged) {
			scrollToPage(position.pageIndex);
		}
	}

	async function goNext() {
		await navigateByStep(1);
	}

	async function goPrevious() {
		await navigateByStep(-1);
	}

	watch(isOpen, (open) => {
		if (!open) {
			selectedProductInfo.value = null;
			canGoNext.value = false;
			canGoPrev.value = false;
		}
	});

	watch(pages, () => {
		metadataCache.clear();
	});

	return {
		isOpen,
		selectedProductInfo,
		currentPageIndex,
		currentProductIndex,
		canGoNext,
		canGoPrev,
		open,
		close,
		goNext,
		goPrevious,
	};
}

export function provideLeafletProductNavigation(
	navigation: ReturnType<typeof useLeafletProductNavigation>,
) {
	provide(leafletProductNavigationKey, navigation);
}

export function useLeafletProductNavigationInject() {
	const navigation = inject(leafletProductNavigationKey);

	if (!navigation) {
		throw new Error('useLeafletProductNavigationInject must be used within a component that called provideLeafletProductNavigation');
	}

	return navigation;
}
